/**
 * @fileoverview Internationalization utilities
 *
 * CONTEXT: Loaded before lang-xx.js, provides t() function for all modules
 * UPSTREAM: HTML loads this before any other application JS
 * DOWNSTREAM: All modules use t() for translated strings
 *
 * @module i18n
 */

/**
 * Safe translation accessor with fallback and plural support.
 *
 * CONTEXT: Core translation function used throughout the application
 * UPSTREAM: Called by all modules that need translated strings
 * DOWNSTREAM: Returns translated string from Lang object
 * FAILURE: Returns key as fallback if translation not found, logs warning
 *
 * @param {string} key - Dot-notation key (e.g., 'filter.buy', 'property.bedroom')
 * @param {Object} [params] - Optional parameters for interpolation
 * @param {number} [params.count] - For pluralization (uses key + 's' if count !== 1)
 * @param {string} [params.fallback] - Fallback value if key not found
 * @returns {string} Translation or fallback
 *
 * @example
 * t('filter.buy')                           // "Buy"
 * t('property.bedroom', {count: 1})         // "bedroom"
 * t('property.bedroom', {count: 3})         // "bedrooms" (auto-pluralize)
 * t('some.missing.key', {fallback: 'N/A'})  // "N/A"
 */
function t(key, params = {}) {
  const { count, fallback } = params;

  // Handle pluralization: if count provided and !== 1, try key + 's' first
  let lookupKey = key;
  if (typeof count === 'number' && count !== 1) {
    lookupKey = key + 's'; // Try plural form first (e.g., 'property.bedrooms')
  }

  // Navigate to the value
  const keys = lookupKey.split('.');
  let value = window.Lang;

  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      // Plural form not found, try singular
      if (lookupKey !== key) {
        return t(key, { ...params, count: undefined }); // Retry without plural
      }
      console.warn('[i18n] Missing translation:', key);
      return fallback || key;
    }
  }

  return value;
}

// Export to global scope
window.t = t;
